import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useT } from '../../i18n';
import type { TranslationKey } from '../../i18n';
import type {
  CriteriaBasket,
  InquiryCriteriaResponse,
  InquiryCriterionEntry,
  InquiryOutcome,
  Stage,
} from '../../types/api';
import { Button } from '../Button';
import { Modal } from '../Modal';
import { Skeleton } from '../Skeleton';
import { StatusPill } from '../StatusPill';
import { TextArea } from '../TextArea';
import { useToast } from '../Toast';
import styles from './CriteriaSection.module.css';

export interface CriteriaSectionProps {
  inquiryId: string;
  /** Issue #26 — only the manufacturer scores criteria; integrators read. */
  canEdit?: boolean;
  /** Issue #49 — stage gating: hidden on VALIDATION, editable on
   *  VERIFICATION, read-only afterwards. */
  stage: Stage;
  outcome?: InquiryOutcome;
}

/** Basket pill reuses the existing confidence-basket tokens (no new colors). */
export const BASKET_TONE: Record<CriteriaBasket, 'confidence_k90' | 'confidence_k50' | 'confidence_k10'> = {
  GREEN: 'confidence_k90',
  YELLOW: 'confidence_k50',
  RED: 'confidence_k10',
};

export const BASKET_LABEL_KEY: Record<CriteriaBasket, TranslationKey> = {
  GREEN: 'criteria.basketGreen',
  YELLOW: 'criteria.basketYellow',
  RED: 'criteria.basketRed',
};

export function criteriaQueryKey(inquiryId: string) {
  return ['inquiries', inquiryId, 'criteria'] as const;
}

/** Segmented 1..5 control. Accessible radiogroup with arrow-key support. */
function ScorePicker({
  value,
  disabled,
  ariaLabel,
  onChange,
}: {
  value: number | null;
  disabled: boolean;
  ariaLabel: string;
  onChange: (score: number) => void;
}) {
  const { t } = useT();

  const move = (delta: number) => {
    const next = Math.min(5, Math.max(1, (value ?? (delta > 0 ? 0 : 6)) + delta));
    onChange(next);
  };

  return (
    <div className={styles.picker} role="radiogroup" aria-label={ariaLabel}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={t('criteria.scoreOption', { score: n })}
          tabIndex={value === n || (value === null && n === 1) ? 0 : -1}
          disabled={disabled}
          className={`${styles.segment} ${value === n ? styles.segmentActive : ''}`}
          onClick={() => onChange(n)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
              e.preventDefault();
              move(1);
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
              e.preventDefault();
              move(-1);
            }
          }}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

function CriterionRow({
  entry,
  inquiryId,
  canEdit,
}: {
  entry: InquiryCriterionEntry;
  inquiryId: string;
  canEdit: boolean;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [note, setNote] = useState(entry.note ?? '');
  const label = locale === 'ru' ? entry.labelRu : entry.labelEn;

  const mutation = useMutation({
    mutationFn: (input: { score: number; note: string | null }) =>
      api.put<InquiryCriteriaResponse>(`/inquiries/${inquiryId}/criteria/${entry.key}`, input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: criteriaQueryKey(inquiryId) });
      const prev = queryClient.getQueryData<InquiryCriteriaResponse>(criteriaQueryKey(inquiryId));
      if (prev) {
        queryClient.setQueryData<InquiryCriteriaResponse>(criteriaQueryKey(inquiryId), {
          ...prev,
          criteria: prev.criteria.map((c) =>
            c.key === entry.key ? { ...c, score: input.score, note: input.note } : c,
          ),
        });
      }
      return { prev };
    },
    onError: (err, _input, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(criteriaQueryKey(inquiryId), ctx.prev);
      setNote(entry.note ?? '');
      const msg = err instanceof ApiError ? err.message : undefined;
      toast.error(t('criteria.toastFailed'), msg);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(criteriaQueryKey(inquiryId), data);
      // The score lands in the inquiry timeline as SET_CRITERION
      void queryClient.invalidateQueries({ queryKey: ['inquiries', inquiryId, 'transitions'] });
      toast.success(t('criteria.toastSaved'));
    },
  });

  const dirty = note.trim() !== (entry.note ?? '');

  return (
    <li className={styles.row}>
      <div className={styles.rowMain}>
        <span className={styles.label}>{label}</span>
        <ScorePicker
          value={entry.score}
          disabled={!canEdit || mutation.isPending}
          ariaLabel={label}
          onChange={(score) => mutation.mutate({ score, note: note.trim() || null })}
        />
      </div>
      {/* The note field only appears once a score exists — keeps the list compact. */}
      {entry.score !== null && (
        <div className={styles.noteRow}>
          <TextArea
            label={t('criteria.noteLabel')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('criteria.notePlaceholder')}
            rows={2}
            fullWidth
            disabled={!canEdit || mutation.isPending}
          />
          {canEdit && dirty && (
            <Button
              size="sm"
              variant="secondary"
              loading={mutation.isPending}
              onClick={() => mutation.mutate({ score: entry.score!, note: note.trim() || null })}
            >
              {t('criteria.saveNote')}
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

function AggregateSummary({ data }: { data: InquiryCriteriaResponse }) {
  const { t } = useT();
  const { aggregate } = data;
  if (!aggregate || aggregate.filled === 0 || !aggregate.basket) return null;
  return (
    <span className={styles.summary}>
      <span className={styles.average}>
        {t('criteria.average')} {aggregate.average!.toFixed(1)}
      </span>
      <StatusPill tone={BASKET_TONE[aggregate.basket]}>
        {t(BASKET_LABEL_KEY[aggregate.basket])}
      </StatusPill>
      <span className={styles.filled}>
        {t('criteria.filled', { filled: aggregate.filled, total: aggregate.total })}
      </span>
    </span>
  );
}

/**
 * Issue #26 — verification criteria. MOCK catalog until the customer's list
 * arrives. Scores save per action with optimistic update + rollback; the
 * basket is a suggestion only and never writes back to Inquiry.confidence.
 *
 * Issue #49 — collapsed to a compact row + button opening a modal (same
 * pattern as AttachmentsSection #43), and gated by stage: hidden on
 * VALIDATION, editable on VERIFICATION, read-only afterwards (incl. WON/LOST).
 */
export function CriteriaSection({
  inquiryId,
  canEdit = false,
  stage,
  outcome = 'NONE',
}: CriteriaSectionProps) {
  const { t } = useT();
  const [modalOpen, setModalOpen] = useState(false);

  const criteriaQ = useQuery<InquiryCriteriaResponse>({
    queryKey: criteriaQueryKey(inquiryId),
    queryFn: () => api.get<InquiryCriteriaResponse>(`/inquiries/${inquiryId}/criteria`),
  });

  // Before verification there is nothing to score — hide the block entirely.
  if (stage === 'VALIDATION') return null;

  const editable = canEdit && stage === 'VERIFICATION' && outcome === 'NONE';
  const readOnly = !editable;
  const filled = criteriaQ.data?.aggregate.filled ?? 0;

  return (
    <section aria-label={t('criteria.title')} className={styles.section}>
      <div className={styles.head}>
        <h3 className={styles.title}>{t('criteria.title')}</h3>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<ClipboardCheck size={14} />}
          onClick={() => setModalOpen(true)}
        >
          {/* "Fill in criteria" only when the action is actually possible —
              read-only viewers get the neutral label. */}
          {filled === 0 && editable ? t('criteria.openEmpty') : t('criteria.open')}
        </Button>
      </div>

      {criteriaQ.isLoading ? (
        <div className={styles.skeleton}>
          <Skeleton width="60%" />
        </div>
      ) : criteriaQ.data ? (
        <div className={styles.compactLine}>
          <AggregateSummary data={criteriaQ.data} />
          {filled === 0 && <span className={styles.empty}>{t('criteria.empty')}</span>}
        </div>
      ) : null}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={t('criteria.title')}
        size="lg"
        footer={
          <Button onClick={() => setModalOpen(false)}>{t('common.done')}</Button>
        }
      >
        <div className={styles.modalBody}>
          {criteriaQ.data && (
            <div className={styles.modalSummary}>
              <AggregateSummary data={criteriaQ.data} />
              {filled === 0 && <span className={styles.empty}>{t('criteria.empty')}</span>}
            </div>
          )}
          {/* The stage hint is truthful only when the stage/outcome is the
              reason for read-only — for integrators it's their role, not the stage. */}
          {readOnly && canEdit && (
            <p className={styles.readOnlyNote}>{t('criteria.readOnlyStage')}</p>
          )}
          <ul className={styles.list}>
            {(criteriaQ.data?.criteria ?? []).map((entry) => (
              <CriterionRow key={entry.key} entry={entry} inquiryId={inquiryId} canEdit={editable} />
            ))}
          </ul>
        </div>
      </Modal>
    </section>
  );
}
